/**
 * Optimizer Control Center — build a paste upsert body + run paste across targets
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FlowOptimizerConfig } from '@trafficpulse/shared';

import { upsertConfig } from '@/features/flow-optimizer/services/flow-optimizer-api';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';

import type { PasteFieldKey, PortableOptimizerConfig } from './optimizer-clipboard.types';

export type PasteBody = Partial<FlowOptimizerConfig> & { flowId: string };

/**
 * Recipe + the target's own flowId, restricted to the chosen fields. `enabled`,
 * `killSwitch` and `standardJson` are never included, and any unchecked field is
 * omitted too: the backend partial-upsert only writes the keys it receives, so a
 * target keeps everything we leave out (run-state, snapshot and unchecked recipe
 * parts all stay put).
 */
export function buildPasteBody(
  recipe: PortableOptimizerConfig,
  targetFlowId: string,
  fields: Set<PasteFieldKey>,
): PasteBody {
  const body: PasteBody = { flowId: targetFlowId };
  if (fields.has('mode')) body.mode = recipe.mode;
  if (fields.has('formula')) body.formula = recipe.formula;
  if (fields.has('excludeRules')) {
    body.excludeRules = recipe.excludeRules;
    body.capacityRules = recipe.capacityRules ?? [];
  }
  if (fields.has('interval')) body.intervalMin = recipe.intervalMin;
  if (fields.has('weights')) {
    body.minWeight = recipe.minWeight;
    body.maxWeight = recipe.maxWeight;
  }
  // Mirrors attribution only — a source without a package never unassigns.
  if (fields.has('package') && recipe.packageId) body.packageId = recipe.packageId;
  return body;
}

export interface PasteOutcome {
  ok: number;
  failed: number;
}

/** Upsert the chosen recipe fields onto every target; tolerates per-target failures. */
export async function pasteToTargets(
  recipe: PortableOptimizerConfig,
  targets: OptimizerRow[],
  fields: Set<PasteFieldKey>,
): Promise<PasteOutcome> {
  const results = await Promise.allSettled(
    targets.map((t) =>
      upsertConfig(t.campaignId, buildPasteBody(recipe, t.flowId, fields), t.provider),
    ),
  );
  const ok = results.filter((r) => r.status === 'fulfilled').length;
  return { ok, failed: results.length - ok };
}
